import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Modal, Input, Select, Badge } from "@/components/UI";
import { Calendar as CalendarIcon, StickyNote, Plus, ChevronLeft, ChevronRight, Trash2, DollarSign, ArrowRight, Repeat } from "lucide-react";
import { Link } from "react-router-dom";
import { todayTR } from "@/lib/date";

/**
 * ChecksCalendar — Kontrol Paneli takvimi
 * - Aylık takvim: her günde vadesi gelen çek(ler) + kişisel notlar
 * - Not ekle/sil/gösterme, çek detay popover
 */
const TR_MONTHS = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const TR_DAY_HEADS = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];

export default function ChecksCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-11
  const [data, setData] = useState({ days: {}, summary: {} });
  const [events, setEvents] = useState({ days: {} });
  const [openNoteDate, setOpenNoteDate] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [openChecksDate, setOpenChecksDate] = useState(null);
  const [openEventModal, setOpenEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", date: "", recurrence: "none", repeat_mode: "forever", repeat_count: 1, note: "" });

  const ymStr = useMemo(() => `${year}-${String(month + 1).padStart(2, "0")}`, [year, month]);
  const load = useCallback(async () => {
    try {
      const [checks, evts] = await Promise.all([
        api.get("/dashboard/checks-calendar", { month: ymStr }),
        api.get("/dashboard/calendar-events", { month: ymStr }),
      ]);
      setData(checks); setEvents(evts);
    } catch (e) { console.error(e); }
  }, [ymStr]);
  useEffect(() => { load(); }, [load]);

  const monthGrid = useMemo(() => {
    const first = new Date(year, month, 1);
    const startDay = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [year, month]);

  const nav = (delta) => {
    let ny = year; let nm = month + delta;
    if (nm < 0) { nm = 11; ny -= 1; } else if (nm > 11) { nm = 0; ny += 1; }
    setYear(ny); setMonth(nm);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post("/dashboard/notes", { date: openNoteDate, text: noteText.trim(), color: "amber" });
      setNoteText(""); setOpenNoteDate(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Not eklenemedi"); }
  };
  const delNote = async (id) => {
    if (!window.confirm("Not silinsin mi?")) return;
    try { await api.del(`/dashboard/notes/${id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };
  const saveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.date) { alert("Açıklama ve tarih zorunlu"); return; }
    try {
      await api.post("/dashboard/calendar-events", {
        title: eventForm.title.trim(),
        date: eventForm.date,
        recurrence: eventForm.recurrence,
        repeat_count: eventForm.recurrence !== "none" && eventForm.repeat_mode === "count" ? parseInt(eventForm.repeat_count) : null,
        note: eventForm.note || null,
      });
      setOpenEventModal(false);
      setEventForm({ title: "", date: "", recurrence: "none", repeat_mode: "forever", repeat_count: 1, note: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const delEvent = async (id) => {
    if (!window.confirm("Bu etkinlik silinsin mi? (Tüm tekrarları etkilenir)")) return;
    try { await api.del(`/dashboard/calendar-events/${id}`); load(); } catch (e) { alert(e.response?.data?.detail); }
  };

  const todayISO = todayTR();
  const openChecksData = openChecksDate ? (data.days?.[openChecksDate] || { checks: [], notes: [] }) : null;

  return (
    <Card className="p-4 mb-4" data-testid="checks-calendar">
      <div className="flex items-center gap-3 mb-3">
        <CalendarIcon size={16} className="text-indigo-600"/>
        <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: "'Manrope', sans-serif" }}>Çek Takvimi & Kişisel Notlar</h3>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => nav(-1)} className="p-1 rounded hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft size={14}/></button>
          <div className="text-sm font-semibold w-32 text-center">{TR_MONTHS[month]} {year}</div>
          <button onClick={() => nav(1)} className="p-1 rounded hover:bg-slate-100" data-testid="cal-next"><ChevronRight size={14}/></button>
          <button onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); }} className="text-[10px] font-semibold px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100">Bugün</button>
          <button onClick={() => { setEventForm({ ...eventForm, date: new Date().toISOString().slice(0,10) }); setOpenEventModal(true); }} className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1" data-testid="cal-add-event"><Plus size={11}/> İşlem Ekle</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <MiniStat label="Aylık Tahsilat (Çek)" value={`${(data.summary?.total_incoming || 0).toLocaleString("tr-TR", {minimumFractionDigits:2})} TL`} color="emerald"/>
        <MiniStat label="Aylık Ödeme (Çek)" value={`${(data.summary?.total_outgoing || 0).toLocaleString("tr-TR", {minimumFractionDigits:2})} TL`} color="rose"/>
        <MiniStat label="Toplam Çek Adedi" value={data.summary?.check_count || 0} color="indigo"/>
        <MiniStat label="Kişisel Notlar" value={data.summary?.note_count || 0} color="amber"/>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {TR_DAY_HEADS.map(d => <div key={d} className="text-[10px] font-bold text-slate-500 uppercase text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {monthGrid.map((day, i) => {
          if (day === null) return <div key={i} className="min-h-[80px]"/>;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const cell = data.days?.[iso] || { checks: [], notes: [] };
          const hasChecks = cell.checks.length > 0;
          const hasNotes = cell.notes.length > 0;
          const isToday = iso === todayISO;
          const totalIncome = cell.checks.filter(c => c.type === "INCOME").reduce((s, c) => s + c.amount, 0);
          const totalExpense = cell.checks.filter(c => c.type === "EXPENSE").reduce((s, c) => s + c.amount, 0);
          const dayEvents = events.days?.[iso] || [];
          const hasEvents = dayEvents.length > 0;
          return (
            <div key={i} className={`min-h-[80px] border rounded-lg p-1.5 text-[10px] cursor-pointer transition ${isToday ? "border-indigo-500 bg-indigo-50/50" : "border-slate-200 bg-white hover:border-indigo-300"} ${hasChecks || hasNotes || hasEvents ? "shadow-sm" : ""}`}
                 onClick={() => hasChecks || hasNotes || hasEvents ? setOpenChecksDate(iso) : setOpenNoteDate(iso)}
                 data-testid={`cal-day-${iso}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`font-bold ${isToday ? "text-indigo-700" : "text-slate-700"}`}>{day}</span>
                <button onClick={(e) => { e.stopPropagation(); setOpenNoteDate(iso); }} className="opacity-40 hover:opacity-100" title="Not ekle">
                  <StickyNote size={10}/>
                </button>
              </div>
              {hasChecks && (
                <div className="space-y-0.5">
                  {totalIncome > 0 && <div className="bg-emerald-100 text-emerald-800 rounded px-1 py-0.5 text-[9px] font-mono truncate" title={`${cell.checks.filter(c=>c.type==='INCOME').length} tahsilat`}>+{totalIncome.toLocaleString("tr-TR", {maximumFractionDigits:0})} ₺</div>}
                  {totalExpense > 0 && <div className="bg-rose-100 text-rose-800 rounded px-1 py-0.5 text-[9px] font-mono truncate" title={`${cell.checks.filter(c=>c.type==='EXPENSE').length} ödeme`}>−{totalExpense.toLocaleString("tr-TR", {maximumFractionDigits:0})} ₺</div>}
                  <div className="text-[9px] text-slate-500">{cell.checks.length} çek</div>
                </div>
              )}
              {hasNotes && (
                <div className="mt-0.5 space-y-0.5">
                  {cell.notes.slice(0, 2).map(n => <div key={n.id} className="bg-amber-100 text-amber-900 rounded px-1 py-0.5 text-[9px] truncate" title={n.text}>📌 {n.text}</div>)}
                  {cell.notes.length > 2 && <div className="text-[9px] text-slate-500">+{cell.notes.length - 2} not</div>}
                </div>
              )}
              {hasEvents && (
                <div className="mt-0.5 space-y-0.5">
                  {dayEvents.slice(0, 2).map(ev => <div key={ev.id} className="bg-emerald-100 text-emerald-900 rounded px-1 py-0.5 text-[9px] truncate flex items-center gap-0.5" title={`${ev.title}${ev.recurrence !== "none" ? ` (${ev.recurrence})` : ""}`} data-testid={`cal-event-${iso}-${ev.id}`}>{ev.recurrence !== "none" && <Repeat size={7}/>} {ev.title}</div>)}
                  {dayEvents.length > 2 && <div className="text-[9px] text-slate-500">+{dayEvents.length - 2} etkinlik</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Not ekleme modalı */}
      {openNoteDate && (
        <Modal open={true} onClose={() => { setOpenNoteDate(null); setNoteText(""); }} title={`Not Ekle — ${openNoteDate}`} size="md">
          <Input label="Not içeriği" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Örn: Toplantı, hatırlatma, önemli tarih..." data-testid="note-text-input" autoFocus/>
          <div className="flex justify-end gap-2 mt-3">
            <Btn variant="secondary" onClick={() => { setOpenNoteDate(null); setNoteText(""); }}>Vazgeç</Btn>
            <Btn onClick={addNote} data-testid="note-save"><Plus size={12}/> Ekle</Btn>
          </div>
        </Modal>
      )}

      {/* Gün detay modalı: çekler + notlar */}
      {openChecksDate && (
        <Modal open={true} onClose={() => setOpenChecksDate(null)} title={`${openChecksDate} — Çekler & Notlar & Etkinlikler`} size="lg">
          <div className="space-y-3">
            {(events.days?.[openChecksDate] || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1.5 flex items-center gap-1"><Repeat size={11}/> Etkinlikler ({(events.days?.[openChecksDate] || []).length})</div>
                <div className="space-y-1.5">
                  {(events.days?.[openChecksDate] || []).map(ev => (
                    <div key={ev.id} className="border border-emerald-200 bg-emerald-50/60 rounded p-2 text-xs flex items-start justify-between" data-testid={`event-detail-${ev.id}`}>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-800 flex items-center gap-1">{ev.title}
                          {ev.recurrence !== "none" && <span className="text-[9px] bg-emerald-200 text-emerald-800 px-1 rounded">{ev.recurrence === "daily" ? "Her Gün" : ev.recurrence === "weekly" ? "Her Hafta" : ev.recurrence === "monthly" ? "Her Ay" : "Her Yıl"}{ev.repeat_count ? ` × ${ev.repeat_count}` : " (sürekli)"}</span>}
                        </div>
                        {ev.note && <div className="text-[10px] text-slate-500 italic mt-0.5">"{ev.note}"</div>}
                        {ev.start_date && ev.start_date !== openChecksDate && <div className="text-[9px] text-slate-400 mt-0.5">Başlangıç: {ev.start_date}</div>}
                      </div>
                      <button onClick={() => delEvent(ev.id)} className="text-rose-500 hover:bg-rose-100 p-1 rounded ml-2" data-testid={`delete-event-${ev.id}`}><Trash2 size={11}/></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {openChecksData.checks.length > 0 && (
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1.5 flex items-center gap-1"><DollarSign size={11}/> Vadesi Gelen Çekler ({openChecksData.checks.length})</div>
                <div className="space-y-1.5">
                  {openChecksData.checks.map(c => (
                    <div key={c.id} className={`border rounded p-2 text-xs ${c.type === "INCOME" ? "bg-emerald-50/50 border-emerald-200" : "bg-rose-50/50 border-rose-200"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-slate-800">
                          <Badge level={c.type === "INCOME" ? "ok" : "missing"}>{c.type === "INCOME" ? "Tahsilat" : "Ödeme"}</Badge>
                          <span className="ml-2">{c.counterparty || "—"}</span>
                        </div>
                        <div className={`font-mono font-bold ${c.type === "INCOME" ? "text-emerald-700" : "text-rose-700"}`}>
                          {c.type === "INCOME" ? "+" : "−"}{c.amount.toLocaleString("tr-TR", {minimumFractionDigits:2})} TL
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-600">
                        🏦 {c.bank_name} · Çek No: <span className="font-mono">{c.check_number}</span> · Firma: {c.check_company}
                      </div>
                      {c.note && <div className="text-[10px] text-slate-500 italic mt-0.5">"{c.note}"</div>}
                      <Link to="/finance"><Btn size="sm" variant="secondary" className="mt-1"><ArrowRight size={10}/> Muhasebe'de aç</Btn></Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {openChecksData.notes.length > 0 && (
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1.5 flex items-center gap-1"><StickyNote size={11}/> Kişisel Notlar ({openChecksData.notes.length})</div>
                <div className="space-y-1.5">
                  {openChecksData.notes.map(n => (
                    <div key={n.id} className="border border-amber-200 bg-amber-50/60 rounded p-2 text-xs flex items-start justify-between">
                      <div className="flex-1"><span className="mr-1">📌</span>{n.text}</div>
                      <button onClick={() => delNote(n.id)} className="text-rose-500 hover:bg-rose-100 p-1 rounded ml-2" data-testid={`delete-note-${n.id}`}><Trash2 size={11}/></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="pt-2 border-t flex gap-2">
              <Btn size="sm" onClick={() => { setOpenChecksDate(null); setOpenNoteDate(openChecksDate); }} data-testid="add-note-from-detail"><Plus size={11}/> Bu güne not ekle</Btn>
              <Btn size="sm" variant="secondary" onClick={() => { setEventForm({ ...eventForm, date: openChecksDate }); setOpenChecksDate(null); setOpenEventModal(true); }} data-testid="add-event-from-detail"><Repeat size={11}/> Bu güne etkinlik ekle</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* İşlem Ekle modalı (tekrarlı etkinlikler) */}
      {openEventModal && (
        <Modal open={true} onClose={() => setOpenEventModal(false)} title="İşlem / Etkinlik Ekle" size="md">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Input label="Açıklama *" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Fabrika Kirası Hatırlatması, Toplantı..." data-testid="event-title" autoFocus/></div>
            <Input label="Tarih *" type="date" value={eventForm.date} onChange={e => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date"/>
            <Select label="Tekrar" value={eventForm.recurrence} onChange={e => setEventForm({ ...eventForm, recurrence: e.target.value })} data-testid="event-recurrence">
              <option value="none">Tekrar Olmasın</option>
              <option value="daily">Her Gün</option>
              <option value="weekly">Her Hafta</option>
              <option value="monthly">Her Ay</option>
              <option value="yearly">Her Yıl</option>
            </Select>
            {eventForm.recurrence !== "none" && (<>
              <Select label="Tekrar Sayısı" value={eventForm.repeat_mode} onChange={e => setEventForm({ ...eventForm, repeat_mode: e.target.value })} data-testid="event-repeat-mode">
                <option value="forever">Sürekli</option>
                <option value="count">X Kere Tekrarla</option>
              </Select>
              {eventForm.repeat_mode === "count" && <Input label="Kaç Kere?" type="number" min="1" value={eventForm.repeat_count} onChange={e => setEventForm({ ...eventForm, repeat_count: e.target.value })} data-testid="event-repeat-count"/>}
            </>)}
            <div className="col-span-2"><Input label="Not (opsiyonel)" value={eventForm.note} onChange={e => setEventForm({ ...eventForm, note: e.target.value })} data-testid="event-note"/></div>
          </div>
          <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 mt-3 text-amber-800">
            ⚠ Bu etkinlik yalnızca <strong>hatırlatma</strong> amaçlıdır. Gelir/Gider tablosuna otomatik kayıt oluşturmaz.
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Btn variant="secondary" onClick={() => setOpenEventModal(false)}>Vazgeç</Btn>
            <Btn onClick={saveEvent} data-testid="event-save"><Plus size={12}/> Kaydet</Btn>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function MiniStat({ label, value, color = "indigo" }) {
  const themes = {
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded border p-2 ${themes[color]}`}>
      <div className="text-[9px] uppercase tracking-wider font-semibold opacity-70 mb-0.5">{label}</div>
      <div className="text-sm font-bold font-mono">{value}</div>
    </div>
  );
}
